import { useMachine } from "@xstate/react";
import { useCallback, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useGetInvitation } from "@elation/api-client/src/stitch-web";

import { navigationV2 } from "@src/shared/navigation/v2";
import { captureMessage } from "@src/shared/utils/observability/sentry";

import { type SignUpStepId } from "../../domain";
import {
  INVITATION_PARAM,
  type SignUpAccountValues,
  STEP_BY_NUMBER,
  STEP_NUMBER_BY_ID,
  STEP_PARAM,
  selectProvisioningView,
  signUpProvisioningApi,
  signUpProvisioningMachine,
} from "../../domain/v2";

function readInitialStep(searchParams: URLSearchParams): SignUpStepId {
  return STEP_BY_NUMBER[Number(searchParams.get(STEP_PARAM))] ?? "profile";
}

export function useAccountCreationFlow() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const invitationId = searchParams.get(INVITATION_PARAM);

  const [snapshot, send] = useMachine(signUpProvisioningMachine, {
    input: {
      ...signUpProvisioningApi,
      initialStep: readInitialStep(searchParams),
      invitationId,
    },
  });

  const invitationQuery = useGetInvitation(invitationId ?? "", {
    query: { enabled: Boolean(invitationId), retry: false },
  });
  const invitation = invitationQuery.data?.data;

  const activeStep = snapshot.context.activeStep;
  const isCreatingAccount = snapshot.matches("creatingAccount");
  const isInProvisioning = snapshot.matches("provisioning");

  const setActiveStep = useCallback(
    (step: SignUpStepId) => {
      send({ type: "SET_STEP", step });
    },
    [send],
  );

  // The machine owns the step; the URL merely reflects it so refresh/deep-link
  // resumes at the right place. This is the one unavoidable machine↔router seam.
  // `invitationId` rides along untouched so every step stays in invite mode.
  useEffect(() => {
    const target = String(STEP_NUMBER_BY_ID[activeStep]);
    if (searchParams.get(STEP_PARAM) === target) return;

    const next = new URLSearchParams(searchParams);
    next.set(STEP_PARAM, target);
    setSearchParams(next, { replace: true });
  }, [activeStep, searchParams, setSearchParams]);

  useEffect(() => {
    if (snapshot.matches("done")) {
      navigate(navigationV2.home(), { replace: true });
    }

    if (snapshot.matches({ provisioning: "unauthorized" })) {
      navigate(`${navigationV2.login()}?error=User+not+found`, {
        replace: true,
      });
    }
  }, [navigate, snapshot]);

  useEffect(() => {
    const status = snapshot.context.provisioningStatus;
    if (status?.state !== "failed") return;

    console.error("[v2/sign-up] account provisioning failed", {
      failureReason: status.failureReason,
    });
    captureMessage("[v2/sign-up] account provisioning failed", {
      level: "error",
      extra: {
        failureReason: status.failureReason,
      },
    });
  }, [snapshot.context.provisioningStatus]);

  const createAccount = useCallback(
    (values: SignUpAccountValues) => {
      if (isCreatingAccount || isInProvisioning) return;

      send({ type: "SUBMIT_ACCOUNT", values });
    },
    [isCreatingAccount, isInProvisioning, send],
  );

  const complete = useCallback(() => {
    if (snapshot.matches({ provisioning: "succeeded" })) {
      setActiveStep("verify-email");
    }
  }, [setActiveStep, snapshot]);

  const skip = useCallback(() => {
    if (snapshot.matches({ provisioning: "succeeded" })) {
      setActiveStep("verify-email");
      return;
    }

    if (isInProvisioning || activeStep !== "profile") {
      setActiveStep("trust");
    }
  }, [activeStep, isInProvisioning, setActiveStep, snapshot]);

  const checkEmailVerification = useCallback(() => {
    send({ type: "CHECK_VERIFICATION" });
  }, [send]);

  const resendVerificationEmail = useCallback(() => {
    send({ type: "RESEND_EMAIL" });
  }, [send]);

  const finishInvitation = useCallback(() => {
    send({ type: "FINISH" });
  }, [send]);

  const provisioningView = useMemo(
    () => selectProvisioningView(snapshot),
    [snapshot],
  );

  return {
    accountError: snapshot.context.accountError,
    activeStep,
    complete,
    createAccount,
    emailVerificationError: snapshot.context.emailVerificationError,
    enabled: true,
    finishInvitation,
    invitation: invitation
      ? {
          email: invitation.email,
          firstName: invitation.firstName,
          lastName: invitation.lastName,
          practiceName: invitation.practiceName,
        }
      : undefined,
    invitationAcceptError: snapshot.context.invitationAcceptError,
    invitationError: invitationQuery.isError
      ? "This invitation is no longer valid. Ask your practice to send a new one."
      : null,
    isAccountSubmitting: isCreatingAccount,
    isEmailVerificationChecking: snapshot.matches({
      emailVerification: "checking",
    }),
    isAcceptingInvitation: snapshot.matches({
      invitationComplete: "accepting",
    }),
    isEmailVerificationPending:
      snapshot.matches("emailVerification") ||
      snapshot.matches("invitationComplete"),
    isInvitation: invitationId !== null,
    isLoadingInvitation: invitationQuery.isLoading,
    isVerificationEmailResending: snapshot.matches({
      emailVerification: "resending",
    }),
    checkEmailVerification,
    provisioningView,
    resendVerificationEmail,
    setActiveStep,
    skip,
    verificationEmail: snapshot.context.verificationEmail ?? undefined,
  } as const;
}
